import { useEffect, useId, useRef, useState } from 'react';
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import type { NodeStatus } from '@occ/contracts';
import { AnimatePresence, m } from 'framer-motion';
import { Button, type ButtonProps } from './button';

export { DataTable, DataTableColumnHeader, type DataTableProps } from './data-table';

/**
 * Primitivas de interfaz.
 *
 * `Button` ya vive en `./button.tsx`, sobre CVA + shadcn/ui — el resto sigue
 * escrito a mano, pendiente de migrar pieza a pieza, y por eso este archivo
 * sigue existiendo como el punto de entrada único (`../../components/ui`):
 * ningún import de las páginas cambia mientras dure la migración.
 */

const cx = (...classes: Array<string | false | undefined>) => classes.filter(Boolean).join(' ');

export { Button, type ButtonProps };

export function Card({
  title,
  actions,
  children,
  className,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cx(
        'rounded-lg border border-black/5 bg-card shadow-sm dark:border-white/5',
        className,
      )}
    >
      {(title || actions) && (
        <header className="flex items-center justify-between gap-3 px-4 pt-3.5 pb-1">
          <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
          {actions}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

const STATUS_STYLE: Record<NodeStatus, { dot: string; label: string }> = {
  online: { dot: 'bg-emerald-500', label: 'En línea' },
  offline: { dot: 'bg-neutral-400', label: 'Desconectado' },
  unknown: { dot: 'bg-amber-500', label: 'Desconocido' },
};

export function StatusDot({
  status,
  withLabel = false,
}: {
  status: NodeStatus;
  withLabel?: boolean;
}) {
  const style = STATUS_STYLE[status];
  return (
    <span className="inline-flex items-center gap-1.5" title={style.label}>
      <span className={cx('inline-block size-2 rounded-full', style.dot)} aria-hidden />
      {withLabel && <span className="text-xs opacity-80">{style.label}</span>}
    </span>
  );
}

export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'warn' | 'ok';
}) {
  const tones = {
    neutral: 'bg-accent/60 opacity-80',
    warn: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
    ok: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  } as const;
  return (
    <span className={cx('rounded px-1.5 py-0.5 text-[11px] leading-4', tones[tone])}>
      {children}
    </span>
  );
}

export function Field({
  label,
  hint,
  icon,
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  hint?: string;
  /** Adorno a la izquierda del input — un icono de lucide-react, típicamente. */
  icon?: ReactNode;
}) {
  return (
    <label className="grid gap-1 text-sm">
      <span className="opacity-70">{label}</span>
      <div className="relative">
        {icon && (
          <span className="pointer-events-none absolute inset-y-0 left-0 grid place-items-center pl-2.5 opacity-50">
            {icon}
          </span>
        )}
        <input
          {...props}
          className={cx(
            'w-full rounded bg-accent/40 px-2.5 py-1.5 shadow-sm outline-none',
            'focus:shadow-[0_0_0_2px_var(--ring)]',
            icon ? 'pl-8' : undefined,
            className,
          )}
        />
      </div>
      {hint && <span className="text-xs opacity-60">{hint}</span>}
    </label>
  );
}

export function Select({
  label,
  icon,
  children,
  className,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & {
  label: string;
  /** Adorno a la izquierda, igual que en `Field` — un icono de lucide-react. */
  icon?: ReactNode;
}) {
  return (
    <label className="grid gap-1 text-sm">
      <span className="opacity-70">{label}</span>
      <div className="relative">
        {icon && (
          <span className="pointer-events-none absolute inset-y-0 left-0 z-10 grid place-items-center pl-2.5 opacity-50">
            {icon}
          </span>
        )}
        <select
          {...props}
          className={cx(
            'w-full rounded bg-accent/40 px-2.5 py-1.5 shadow-sm outline-none',
            'focus:shadow-[0_0_0_2px_var(--ring)]',
            icon ? 'pl-8' : undefined,
            className,
          )}
        >
          {children}
        </select>
      </div>
    </label>
  );
}

export function Alert({
  tone = 'error',
  children,
}: {
  tone?: 'error' | 'info';
  children: ReactNode;
}) {
  return (
    <p
      role={tone === 'error' ? 'alert' : undefined}
      className={cx(
        'rounded px-3 py-2 text-sm shadow-sm',
        tone === 'error' ? 'bg-destructive/10 text-destructive' : 'bg-accent/50 opacity-80',
      )}
    >
      {children}
    </p>
  );
}

export function Loading({ label = 'Cargando…' }: { label?: string }) {
  return <p className="text-sm opacity-60">{label}</p>;
}

// ── Añadidos del Módulo 8 completo ──────────────────────────────────────────

export function Textarea({
  label,
  hint,
  className,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { label?: string; hint?: string }) {
  return (
    <label className="grid gap-1 text-sm">
      {label && <span className="opacity-70">{label}</span>}
      <textarea
        {...props}
        className={cx(
          'rounded bg-accent/40 px-2.5 py-1.5 shadow-sm',
          'font-mono text-[13px] leading-5 outline-none focus:shadow-[0_0_0_2px_var(--ring)]',
          className,
        )}
      />
      {hint && <span className="text-xs opacity-60">{hint}</span>}
    </label>
  );
}

export function Checkbox({
  label,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm">
      <input type="checkbox" {...props} className="size-3.5 accent-[var(--occ-brand)]" />
      <span>{label}</span>
    </label>
  );
}

/**
 * Aviso que no es un error.
 *
 * Existe separado de `Alert` porque una capacidad que llega en otra fase no debe
 * pintarse en rojo: quien la ve tiene que distinguir "esto se rompió" de "esto
 * no está todavía", y el color es lo primero que lee.
 */
export function Notice({
  tone = 'pending',
  title,
  children,
}: {
  tone?: 'pending' | 'warn';
  title?: ReactNode;
  children: ReactNode;
}) {
  const tones = {
    pending: 'bg-sky-500/10 text-sky-800 dark:text-sky-200',
    warn: 'bg-amber-500/10 text-amber-800 dark:text-amber-200',
  } as const;
  return (
    <div className={cx('grid gap-1 rounded px-3 py-2 text-sm shadow-sm', tones[tone])}>
      {title && <strong className="font-medium">{title}</strong>}
      <div className="opacity-90">{children}</div>
    </div>
  );
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="grid gap-1 py-6 text-center">
      <p className="text-sm">{title}</p>
      {children && <p className="text-xs opacity-60">{children}</p>}
    </div>
  );
}

/** Barra de progreso determinada. `value` en 0–100; `undefined` = indeterminada. */
export function Progress({ value, label }: { value?: number; label?: ReactNode }) {
  const determinate = value !== undefined && Number.isFinite(value);
  const width = determinate ? Math.min(100, Math.max(0, value)) : 100;
  return (
    <div className="grid gap-1">
      {label && <div className="flex justify-between text-xs opacity-70">{label}</div>}
      <div className="h-1.5 overflow-hidden rounded bg-black/10 dark:bg-white/10">
        <div
          className={cx(
            'h-full rounded bg-[var(--occ-brand)]',
            determinate ? 'transition-[width] duration-200' : 'animate-pulse',
          )}
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Copia al portapapeles con acuse.
 *
 * El acuse importa más de lo que parece: donde más se usa esto es en el
 * one-liner de enrollment, que solo se muestra una vez. Sin confirmación visible
 * el técnico no sabe si puede cerrar el diálogo.
 */
export function CopyButton({
  value,
  label = 'Copiar',
  variant = 'secondary',
}: {
  value: string;
  label?: string;
  variant?: ButtonProps['variant'];
}) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1_800);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // Sin permiso de portapapeles (http sin TLS, por ejemplo): se selecciona
      // el texto para que al menos se pueda copiar a mano.
      setCopied(false);
    }
  };

  return (
    <Button variant={variant} onClick={() => void copy()} title="Copiar al portapapeles">
      {copied ? 'Copiado ✓' : label}
    </Button>
  );
}

/** Bloque monoespaciado con botón de copiar. Para comandos listos para pegar. */
export function CodeBlock({ value, label }: { value: string; label?: ReactNode }) {
  return (
    <div className="grid gap-1.5">
      {label && <div className="text-xs uppercase tracking-wide opacity-50">{label}</div>}
      <div className="flex items-start gap-2">
        <pre className="flex-1 overflow-x-auto rounded bg-black/5 dark:bg-white/5 p-3 text-xs leading-5">
          {value}
        </pre>
        <CopyButton value={value} />
      </div>
    </div>
  );
}

export type TabDefinition = {
  id: string;
  label: ReactNode;
  /** Motivo por el que no se puede entrar. Presente = pestaña apagada. */
  disabledReason?: string;
};

export function Tabs({
  tabs,
  active,
  onChange,
}: {
  tabs: TabDefinition[];
  active: string;
  onChange: (id: string) => void;
}) {
  return (
    <div role="tablist" className="flex flex-wrap gap-1">
      {tabs.map((tab) => {
        const selected = tab.id === active;
        return (
          <button
            key={tab.id}
            role="tab"
            type="button"
            aria-selected={selected}
            disabled={tab.disabledReason !== undefined}
            title={tab.disabledReason}
            onClick={() => onChange(tab.id)}
            className={cx(
              '-mb-px rounded-t border-b-2 px-3 py-1.5 text-sm transition',
              'disabled:cursor-not-allowed disabled:opacity-40',
              selected
                ? 'border-[var(--occ-brand)] font-medium'
                : 'border-transparent opacity-70 hover:opacity-100',
            )}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Diálogo modal.
 *
 * Sustituye a `window.confirm` en todo lo que no sea trivial: el nativo no
 * respeta el modo oscuro, no permite explicar qué va a pasar ni pedir que se
 * escriba el nombre de lo que se borra, y en este panel casi todo lo destructivo
 * merece las tres cosas.
 */
export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  wide = false,
  maxWidthClassName,
}: {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  /** Anula `wide` con una clase de ancho propia — para el diálogo puntual que necesita más sitio. */
  maxWidthClassName?: string;
}) {
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // `open` decide si el hijo está montado, no un `return null` antes de
  // `AnimatePresence`: la salida necesita que el modal siga en el árbol
  // mientras se anima, y un `return null` temprano se lo quitaría de encima
  // de un frame a otro, sin dar tiempo a que `exit` corra.
  return (
    <AnimatePresence>
      {open && (
        <m.div
          className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          // Clic en el fondo cierra; clic dentro no debe burbujear hasta aquí.
          onClick={onClose}
        >
          <m.div
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            onClick={(event) => event.stopPropagation()}
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className={cx(
              'flex max-h-[88vh] w-full flex-col overflow-hidden rounded-lg shadow-xl',
              'bg-[var(--occ-surface)] text-[var(--occ-text)]',
              maxWidthClassName ?? (wide ? 'max-w-3xl' : 'max-w-lg'),
            )}
          >
            <header className="flex shrink-0 items-center justify-between gap-3 px-4 pt-3.5 pb-1">
              <h2 id={titleId} className="text-sm font-semibold tracking-tight">
                {title}
              </h2>
              <Button variant="ghost" onClick={onClose} aria-label="Cerrar">
                ✕
              </Button>
            </header>
            <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>
            {footer && (
              <footer className="flex shrink-0 justify-end gap-2 px-4 py-3">{footer}</footer>
            )}
          </m.div>
        </m.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Confirmación de una operación destructiva.
 *
 * `confirmText` obliga a teclear el nombre de lo que se va a perder. Se reserva
 * para lo que no tiene vuelta —borrar un cliente con sus sucursales, borrar un
 * directorio del nodo— porque pedirlo para todo entrena a la gente a copiar y
 * pegar sin leer, que es justo lo contrario de lo que se busca.
 */
export function ConfirmDialog({
  open,
  title,
  confirmLabel = 'Confirmar',
  confirmText,
  danger = true,
  busy = false,
  onConfirm,
  onCancel,
  children,
}: {
  open: boolean;
  title: ReactNode;
  confirmLabel?: string;
  confirmText?: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  children: ReactNode;
}) {
  const [typed, setTyped] = useState('');

  // Se reinicia al abrir: si no, una confirmación anterior dejaría el texto ya
  // escrito y el botón activo antes de leer nada.
  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  const ready = confirmText === undefined || typed.trim() === confirmText;

  return (
    <Modal
      open={open}
      title={title}
      onClose={onCancel}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Cancelar
          </Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            disabled={!ready || busy}
            onClick={onConfirm}
          >
            {busy ? 'Ejecutando…' : confirmLabel}
          </Button>
        </>
      }
    >
      <div className="grid gap-3 text-sm">
        {children}
        {confirmText !== undefined && (
          <Field
            label={`Escribe «${confirmText}» para confirmar`}
            value={typed}
            autoFocus
            onChange={(event) => setTyped(event.target.value)}
          />
        )}
      </div>
    </Modal>
  );
}

/**
 * Aviso efímero de resultado. Se apila arriba a la derecha y desaparece solo.
 *
 * Las acciones de este panel ocurren en un nodo remoto y muchas no cambian nada
 * visible en pantalla (reiniciar un servicio, matar un proceso). Sin un acuse,
 * la única forma de saber que pasó algo sería volver a consultar.
 */
export type ToastTone = 'ok' | 'error';
export type ToastMessage = { id: number; tone: ToastTone; text: string };

export function useToasts() {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const nextId = useRef(1);

  const push = (tone: ToastTone, text: string) => {
    const id = nextId.current++;
    setToasts((current) => [...current, { id, tone, text }]);
    setTimeout(() => setToasts((current) => current.filter((toast) => toast.id !== id)), 6_000);
  };

  return {
    toasts,
    ok: (text: string) => push('ok', text),
    fail: (text: string) => push('error', text),
  };
}

export function Toasts({ toasts }: { toasts: ToastMessage[] }) {
  // El contenedor se queda montado aunque no haya avisos: si se desmontara con
  // el último, `AnimatePresence` no tendría dónde correr su salida y el aviso
  // desaparecería de golpe en vez de deslizarse fuera.
  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed right-4 top-4 z-[60] grid w-80 gap-2"
    >
      <AnimatePresence>
        {toasts.map((toast) => (
          <m.div
            key={toast.id}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 24, transition: { duration: 0.15 } }}
            transition={{ type: 'spring', stiffness: 420, damping: 34 }}
            className={cx(
              'rounded px-3 py-2 text-sm shadow-lg backdrop-blur',
              toast.tone === 'ok'
                ? 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-200'
                : 'bg-destructive/15 text-destructive',
            )}
          >
            {toast.text}
          </m.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
